/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  VStack,
  Text,
  useToast,
} from "@chakra-ui/react";
import FilterPanel from "components/FilterPanel/FilterPanel";
import { useErrorHandler } from "hooks/useErrorHandler";
import { FiDownload } from "react-icons/fi";
import { useBayiOptions } from "hooks/useBayiOptions";
import { useUserRole } from "hooks/useUserRole";

const REPORT_OPTIONS = [
  {
    id: "kiralama-sonlandirilan",
    label: "Kiralama sonlandırılan araçlar",
    excelPath: "/api/raporlar/kiralama-sonlandirilan-araclar/excel",
    filename: "kiralama_sonlandirilan_araclar.xlsx",
  },
  {
    id: "uetds-hesap-acik-arac-kapali",
    label: "UETDS — hesap açık, araç kapalı",
    excelPath: "/api/raporlar/uetds-hesap-acik-arac-kapali/excel",
    filename: "uetds_hesap_acik_arac_kapali.xlsx",
  },
];

export default function Raporlar() {
  const toast = useToast();
  const { handleError } = useErrorHandler();
  const [reportType, setReportType] = useState(REPORT_OPTIONS[0].id);
  const [bayiFilter, setBayiFilter] = useState("");
  const [downloading, setDownloading] = useState(false);
  const userRole = useUserRole();
  const shouldLoadBayiler = userRole === "admin" || userRole === "teknik_eleman";
  const { bayiler } = useBayiOptions(shouldLoadBayiler);

  const selectedReport =
    REPORT_OPTIONS.find((r) => r.id === reportType) || REPORT_OPTIONS[0];

  const filterFields = useMemo(() => {
    const fields = [
      {
        key: "report_type",
        type: "select",
        placeholder: "Rapor türü",
        value: reportType,
        onChange: setReportType,
        options: REPORT_OPTIONS.map((opt) => ({
          label: opt.label,
          value: opt.id,
        })),
      },
    ];
    if (shouldLoadBayiler) {
      fields.push({
        key: "bayi",
        type: "select",
        placeholder: "İş ortağı (tümü)",
        value: bayiFilter,
        onChange: setBayiFilter,
        options: [
          { label: "Tüm iş ortakları", value: "" },
          { label: "ixPilot", value: "null" },
          ...bayiler.map((bayi) => ({
            label: bayi.name,
            value: String(bayi.id),
          })),
        ],
      });
    }
    return fields;
  }, [reportType, bayiFilter, bayiler, shouldLoadBayiler]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (bayiFilter && shouldLoadBayiler) {
        if (bayiFilter === "null" || bayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", bayiFilter);
        }
      }
      const queryString = params.toString();
      const url = `${selectedReport.excelPath}${queryString ? `?${queryString}` : ""}`;
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error("Excel oluşturulamadı");
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = selectedReport.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Excel indirildi",
        description: selectedReport.label,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      handleError(error, "Excel indirme hatası");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box position="relative" mt={{ base: 2, md: 6 }}>
      <VStack align="stretch" spacing={4}>
        <VStack align="start" spacing={2}>
          <Text
            fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
            fontWeight="bold"
            color="white"
            letterSpacing="-0.5px"
          >
            Raporlar
          </Text>
          <Text
            fontSize={{ base: "xs", md: "sm" }}
            color="white"
            fontWeight="normal"
            opacity={0.9}
          >
            Rapor seçin ve Excel olarak indirin
          </Text>
        </VStack>

        <Box
          bg="white"
          borderRadius="2xl"
          boxShadow="md"
          px={{ base: 4, md: 6 }}
          py={{ base: 4, md: 5 }}
        >
          <Box
            sx={{
              "& > div": {
                mt: "0 !important",
                mb: "0 !important",
              },
            }}
          >
          <FilterPanel
            fields={filterFields}
            fieldWidth={{ base: "100%", md: "240px", lg: "280px" }}
            gap={3}
            actions={[
              {
                key: "excel",
                label: "Excel İndir",
                onClick: handleDownload,
                buttonProps: {
                  colorScheme: "green",
                  variant: "solid",
                  leftIcon: <FiDownload />,
                  isLoading: downloading,
                  loadingText: "Hazırlanıyor…",
                },
              },
            ]}
          />
          </Box>
        </Box>
      </VStack>
    </Box>
  );
}
